/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import JoditEditor from "jodit-react";

/*import url and gateway methods */
import { getData, uploadSingleFile, postData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const ConversationTestMailPopup = ({
  setShowAlert,
  setAlertMessage,
  setMessageType,
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  // loggedin user information
  const currentUser = JSON.parse(localStorage.getItem("userInfo"));

  // ------------ hooks for user Inputs --------------------------------
  const [receivermail, setReceivermail] = useState("");
  const [mailSubject, setMailSubject] = useState("Test Mail");
  const [mailDescription, setMailDescription] = useState("");
  const [mailSending, setMailSending] = useState(false);

  /* hook for validation */
  const [errors, setErrors] = useState({});
  const offcanvasBodyRef = useRef(null);

  // simple email validator
  const isValidEmail = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@\"]+\.)+[^<>()[\]\\.,;:\s@\"]{2,})$/i;
    return re.test(String(email).toLowerCase());
  };

  const editor = useRef(null);

  const editorCustomConfig = {
    readonly: false,
    placeholder: "",
    minHeight: 300,
    toolbarSticky: false,
    uploader: {
      insertImageAsBase64URI: false,
    },
    events: {
      afterPaste: async (event) => {
        // take the saved clipboard data
        const clipboardData = event.clipboardData || window.clipboardData;
        const items = clipboardData.items;

        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            const file = items[i].getAsFile();

            // Rename the file using a timestamp
            const timestamp = Date.now();
            const fileExtension = file.type.split("/")[1];
            const newFileName = `image-${timestamp}.${fileExtension}`;

            const renamedFile = new File([file], newFileName, {
              type: file.type,
            });

            try {
              // upload the file data in server
              const requestUrl =
                url.API_BASE_URL +
                url.API_SINGLE_FILE_UPLOAD +
                ``;

              const response = await uploadSingleFile(requestUrl, renamedFile);

              if (response.status) {
                const imageUrl = url.SERVER_URL + response.data.path;

                // Replace blob URL with the server URL
                const editorInstance = editor.current;

                if (editorInstance) {
                  const content = editorInstance.value;
                  const regex = /<img[^>]+src="data:image\/[^">]+"/g;
                  const newContent = content.replace(regex, () => {
                    return `<img src="${imageUrl}"`;
                  });

                  // add in editor content
                  editorInstance.value = newContent;

                  // add in state content
                  setMailDescription(newContent);
                }
              }
            } catch (error) {
              console.error("Image upload failed:", error);
            }
          }
        }
      },
    },
    defaultActionOnPaste: "insert_as_html",
    defaultLineHeight: 1.5,
    enter: "div",
    statusbar: false,
    sizeLG: 900,
    sizeMD: 700,
    sizeSM: 400,
    toolbarAdaptive: false,
    disablePlugins: [
      "image",
      "video",
      "about",
      "classSpan",
      "file",
      "speechRecognize",
      "copyformat",
      "spellcheck",
      "add-new-line",
    ],
    removeButtons: ["link"],
  };

  const editorConfig = useMemo(() => editorCustomConfig, []);

  const validateForm = () => {
    const newErrors = {};

    // check if user has entered any email or not
    if (!receivermail?.trim()) {
      newErrors.receivermail = t("Please enter a mail address!");
    } else if (!isValidEmail(receivermail)) {
      newErrors.receivermail = t("Please enter a valid email address!");
    }

    // check if subject is empty or not
    if (!mailSubject?.trim()) {
      newErrors.mailSubject = t("Please enter a subject!");
    }

    // check if description is empty
    if (!mailDescription?.trim()) {
      newErrors.mailDescription = t("Please enter mail content!");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* function to send test mail */
  const sendTestMailHandler = async () => {
    if (!validateForm()) {
      if (offcanvasBodyRef.current) {
        offcanvasBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    let mailData = {
      receivermails: receivermail,
      subject: mailSubject,
      sender: currentUser._id,
      description: mailDescription,
      isTestMail: true,
    };

    try {
      setMailSending(true);

      let requestUrl =
        url.API_BASE_URL + url.API_ADD_MESSAGE;

      const response = await postData(requestUrl, mailData);

      console.log("Test Mail Response", response);

      setMailSending(false);

      if (response.status) {
        setMessageType("success");
        resetHandler();

        window.bootstrap.Offcanvas.getInstance(
          document.querySelector("#offcanvasTestMail"),
        ).hide();
      } else {
        setMessageType("error");
      }
      setAlertMessage(response.message);
    } catch (error) {
      setMessageType("error");
      setAlertMessage(error.message);
      setMailSending(false);
    }

    setShowAlert(true);
  };

  /* reset */
  const resetHandler = () => {
    setMailDescription("");
    setReceivermail("");
    setMailSubject("Test Mail");
    setErrors({});
  };

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0"
      tabIndex="-1"
      id="offcanvasTestMail"
      aria-labelledby="offcanvasTestMailLabel"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              mail
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasTestMailLabel"
            >
              {t("Send Test Mail")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {t("Test your SMTP configuration")}
            </p>
          </div>
        </div>
        <button
          onClick={resetHandler}
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        ></button>
      </div>
      <div className="offcanvas-body p-4" ref={offcanvasBodyRef}>
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
          <form
            onSubmit={(e) => e.preventDefault()}
            className="d-flex flex-column h-100"
          >
            <div className="fields-container flex-fill">
              {/* <!-- ========== Start mail address Section ========== --> */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="receivermail"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Mail Address")} *
                </label>
                <input
                  type="text"
                  id="receivermail"
                  className={`form-control fs-sm shadow-none ${
                    errors.receivermail
                      ? "is-invalid"
                      : "border border-gray-300"
                  }`}
                  placeholder={t("e.g., recipient@example.com")}
                  value={receivermail}
                  onChange={(e) => {
                    setReceivermail(e.target.value);
                    if (errors.receivermail) {
                      setErrors((prev) => ({ ...prev, receivermail: "" }));
                    }
                  }}
                />
                {errors.receivermail && (
                  <div className="invalid-feedback">{errors.receivermail}</div>
                )}
              </div>

              {/* <!-- ========== Start subject Section ========== --> */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="mailSubject"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Subject")} *
                </label>
                <input
                  type="text"
                  id="mailSubject"
                  placeholder={t("e.g., Test Mail")}
                  className={`form-control fs-sm rounded-lg-2 shadow-none ${
                    errors.mailSubject ? "is-invalid" : "border border-gray-300"
                  }`}
                  value={mailSubject}
                  onChange={(e) => {
                    setMailSubject(e.target.value);
                    if (errors.mailSubject) {
                      setErrors((prev) => ({ ...prev, mailSubject: "" }));
                    }
                  }}
                />
                {errors.mailSubject && (
                  <div className="invalid-feedback">{errors.mailSubject}</div>
                )}
              </div>

              {/* ------- editor section start ------- */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="mailDescription"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Description")} *
                </label>
                <JoditEditor
                  ref={editor}
                  value={mailDescription}
                  config={editorConfig}
                  onBlur={(newContent) => {
                    setMailDescription(newContent);
                    if (errors.mailDescription) {
                      setErrors((prev) => ({ ...prev, mailDescription: "" }));
                    }
                  }}
                  onChange={(newContent, event) => {
                    // Handle change if needed
                  }}
                />
                {errors.mailDescription && (
                  <div className="text-danger mt-2 fs-sm">
                    {errors.mailDescription}
                  </div>
                )}
              </div>
              {/* ------- editor section end ------- */}
            </div>

            {/* send button */}
            <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={sendTestMailHandler}
                style={{ cursor: mailSending ? "not-allowed" : "pointer" }}
                disabled={mailSending ? true : false}
              >
                {mailSending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {t("Sending...")}
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined me-1">send</span>
                    {t("Send Test Mail")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConversationTestMailPopup;
